import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaPlus, 
  FaTrash,
  FaSearch,
  FaMoneyBillAlt,
  FaSave,
  FaTimes
} from 'react-icons/fa';
import { otherIncomeService, type CreateOtherIncomeEntry } from '../services/otherIncomeService';
import { projectService } from '../services/projectService';
import { usePageTitle } from '../hooks/usePageTitle';
import { useHiddenMode } from '../contexts/HiddenModeContext';
import '../css/pages/AddOtherIncome.css';

interface Project {
  id: number;
  name: string;
}

interface IncomeHead {
  id: string;  // CUID
  name: string;
  description?: string;
}

interface IncomeRow extends CreateOtherIncomeEntry {
  tempId: string;
  errors: string[];
}

const AddOtherIncome: React.FC = () => {
  usePageTitle();
  const { isHiddenModeActive } = useHiddenMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const isEditMode = window.location.pathname.includes('/edit/');
  const isViewMode = window.location.pathname.includes('/view/');
  const mode = isEditMode ? 'edit' : isViewMode ? 'view' : 'add';
  
  const [projects, setProjects] = useState<Project[]>([]);
  const [incomeHeads, setIncomeHeads] = useState<IncomeHead[]>([]);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  const [projectSearch, setProjectSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Income Head Creation
  const [showNewIncomeHeadForm, setShowNewIncomeHeadForm] = useState(false);
  const [newIncomeHead, setNewIncomeHead] = useState({
    name: '',
    description: ''
  });
  
  // Income Rows
  const [incomeRows, setIncomeRows] = useState<IncomeRow[]>([
    {
      tempId: `row_${Date.now()}`,
      projectId: 0,
      incomeHeadId: '',  // Empty string for CUID
      incomeDate: new Date().toISOString().split('T')[0],
      narration: '',
      amount: 0,
      errors: []
    }
  ]);

  useEffect(() => {
    loadInitialData();
    if (mode !== 'add' && id) {
      loadOtherIncome(id);  // ID is already a string from URL params
    }
  }, [mode, id]);

  const loadOtherIncome = async (otherIncomeId: string) => {
    try {
      setLoading(true);
      const response = await otherIncomeService.getOtherIncomeById(otherIncomeId);
      
      // Convert the response to income rows format
      if (response) {
        const incomeRow: IncomeRow = {
          tempId: `row_${Date.now()}`,
          projectId: response.projectId || 0,
          incomeHeadId: response.incomeHeadId || '',  // CUID string
          incomeDate: response.incomeDate ? new Date(response.incomeDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          narration: response.narration || '',
          amount: response.amount || 0,
          errors: []
        };
        
        setIncomeRows([incomeRow]);
        
        // Set selected project if available
        if (response.projectId && projects.length > 0) {
          const project = projects.find(p => p.id === response.projectId);
          if (project) {
            setSelectedProject(project);
          }
        }
      }
    } catch (error) {
      console.error('Error loading other income:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadProjects(),
        loadIncomeHeads()
      ]);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const response = await projectService.getProjects();
      setProjects(response.filter((p: any) => p.isActive));
    } catch (error) {
      console.error('Error loading projects:', error);
    }
  };

  const loadIncomeHeads = async () => {
    try {
      const response = await otherIncomeService.getIncomeHeads();
      setIncomeHeads(response);
    } catch (error) {
      console.error('Error loading income heads:', error);
    }
  };

  const filteredProjects = projects.filter(project =>
    project.name.toLowerCase().includes(projectSearch.toLowerCase())
  );

  const handleProjectSelect = (project: Project) => {
    setSelectedProject(project);
    setIncomeRows(rows => 
      rows.map(row => ({ ...row, projectId: project.id, errors: [] }))
    );
  };

  const handleIncomeRowChange = (tempId: string, field: keyof CreateOtherIncomeEntry, value: any) => {
    setIncomeRows(rows => {
      const updatedRows = rows.map(row =>
        row.tempId === tempId
          ? { ...row, [field]: value, errors: [] }
          : row
      );
      return updatedRows;
    });
  };

  const addIncomeRow = () => {
    const newRow: IncomeRow = {
      tempId: `row_${Date.now()}`,
      projectId: selectedProject?.id || 0,
      incomeHeadId: '',  // Empty string for CUID
      incomeDate: new Date().toISOString().split('T')[0],
      narration: '',
      amount: 0,
      errors: []
    };
    setIncomeRows(rows => [...rows, newRow]);
  };

  const removeIncomeRow = (tempId: string) => {
    if (incomeRows.length > 1) {
      setIncomeRows(rows => rows.filter(row => row.tempId !== tempId));
    }
  };

  const validateForm = (): boolean => {
    let isValid = true;
    
    if (!selectedProject) {
      alert('Please select a project');
      return false;
    }

    const updatedRows = incomeRows.map(row => {
      const errors: string[] = [];
      
      if (!row.incomeHeadId) {
        errors.push('Income head is required');
      }
      
      if (!row.incomeDate) {
        errors.push('Income date is required');
      }
      
      if (!row.amount || row.amount <= 0) {
        errors.push('Amount must be greater than 0');
      }
      
      if (errors.length > 0) {
        isValid = false;
      }
      
      return { ...row, errors };
    });

    setIncomeRows(updatedRows);
    return isValid;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);
      
      if (mode === 'edit' && id) {
        // Update existing other income entry
        const row = incomeRows[0];
        const incomeData = {
          projectId: selectedProject!.id,
          incomeHeadId: row.incomeHeadId,
          incomeDate: row.incomeDate,
          narration: row.narration || undefined,
          amount: row.amount
        };

        await otherIncomeService.updateOtherIncome(id, incomeData);
        navigate('/ledger/other');
      } else {
        // Create new other income entries
        const incomeData: CreateOtherIncomeEntry[] = incomeRows.map(row => ({
          projectId: selectedProject!.id,
          incomeHeadId: row.incomeHeadId,
          incomeDate: row.incomeDate,
          narration: row.narration || undefined,
          amount: row.amount
        }));

        await otherIncomeService.createOtherIncomes({ incomes: incomeData });
        navigate('/ledger/other');
      }
    } catch (error) {
      console.error(`Error ${mode === 'edit' ? 'updating' : 'creating'} other income:`, error);
      alert(`Failed to ${mode === 'edit' ? 'update' : 'create'} other income. Please try again.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateIncomeHead = async () => {
    if (!newIncomeHead.name.trim()) {
      alert('Income head name is required');
      return;
    }

    try {
      const createdHead = await otherIncomeService.createIncomeHead({
        name: newIncomeHead.name.trim(),
        description: newIncomeHead.description.trim() || undefined
      });
      
      setIncomeHeads(prev => [...prev, createdHead]);
      setNewIncomeHead({ name: '', description: '' });
      setShowNewIncomeHeadForm(false);
    } catch (error) {
      console.error('Error creating income head:', error);
      alert('Failed to create income head. Please try again.');
    }
  };

  const calculateTotal = (): number => {
    return incomeRows.reduce((total, row) => total + (row.amount || 0), 0);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div style={{ color: '#1a1a1a' }}>
          <h1 style={{ color: '#1a1a1a', margin: '0 0 4px 0' }}>
            <FaMoneyBillAlt className="page-icon" style={{ marginRight: '8px' }} /> 
            {mode === 'edit' ? 'Edit Other Income' : mode === 'view' ? 'View Other Income' : 'Add Other Income'}
          </h1>
        </div>
        <button 
          className="back-btn"
          onClick={() => navigate('/ledger/other')}
        >
          <FaArrowLeft />
        </button>
      </div>

      {/* Project Selection */}
      <div className="form-container">
        <h2>Select Project</h2>
        
        {!selectedProject ? (
          <div className="project-selection">
            <div className="project-search-bar">
              <FaSearch />
              <input
                type="text"
                placeholder="Search projects..."
                value={projectSearch}
                onChange={(e) => setProjectSearch(e.target.value)}
              />
            </div>
            
            <div className="project-list">
              {filteredProjects.map(project => (
                <div
                  key={project.id}
                  className="project-item"
                  onClick={() => handleProjectSelect(project)}
                >
                  <div className="project-info">
                    <h3>{project.name}</h3>
                  </div>
                </div>
              ))}
            </div>
            
            {filteredProjects.length === 0 && (
              <div className="no-projects">
                No projects found matching "{projectSearch}"
              </div>
            )}
          </div>
        ) : (
          <div className="selected-project-info">
            <h3>{selectedProject.name}</h3>
            {mode === 'add' && (
              <button 
                className="change-project-btn"
                onClick={() => setSelectedProject(null)}
              >
                Change Project
              </button>
            )}
          </div>
        )}
      </div>

      {/* Income Head Management */}
      {selectedProject && (
        <div className="form-container">
          <div className="income-head-header">
            <h2>Income Heads</h2>
            <button
              className="add-income-head-btn"
              onClick={() => setShowNewIncomeHeadForm(!showNewIncomeHeadForm)}
            >
              <FaPlus /> Add New Income Head
            </button>
          </div>
          
          {showNewIncomeHeadForm && (
            <div className="new-income-head-form">
              <div className="form-row">
                <div className="form-group">
                  <label>Income Head Name *</label>
                  <input
                    type="text"
                    value={newIncomeHead.name}
                    onChange={(e) => setNewIncomeHead(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Enter income head name"
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <input
                    type="text"
                    value={newIncomeHead.description}
                    onChange={(e) => setNewIncomeHead(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Enter description (optional)"
                  />
                </div>
                <div className="form-actions">
                  <button className="btn-save" onClick={handleCreateIncomeHead}>
                    <FaSave /> Save
                  </button>
                  <button className="btn-cancel" onClick={() => setShowNewIncomeHeadForm(false)}>
                    <FaTimes /> Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Income Entries */}
      {selectedProject && (
        <div className="form-container">
          <div className="income-entries-header">
            <h2>Income Entries</h2>
            {!isViewMode && (
              <button className="add-row-btn" onClick={addIncomeRow}>
                <FaPlus /> Add Row
              </button>
            )}
          </div>
          
          <div className="content-section">
            <table className="standard-table income-table">{/* Add both classes */}
              <thead>
                <tr>
                  <th>Income Head *</th>
                  <th>Date *</th>
                  <th>Narration</th>
                  <th>Amount *</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {incomeRows.map((row, index) => {
                  return (
                  <tr key={row.tempId} className={row.errors.length > 0 ? 'error-row' : ''}>
                    <td>
                      <select
                        value={row.incomeHeadId || ''}
                        onChange={(e) => {
                          // Keep as string - it's a CUID!
                          handleIncomeRowChange(row.tempId, 'incomeHeadId', e.target.value);
                        }}
                        className={row.errors.some(e => e.includes('Income head')) ? 'error' : ''}
                        disabled={isViewMode}
                      >
                        <option value="">Select Income Head</option>
                        {incomeHeads.map(head => (
                          <option key={head.id} value={head.id}>
                            {head.name}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type="date"
                        value={row.incomeDate}
                        onChange={(e) => handleIncomeRowChange(row.tempId, 'incomeDate', e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        className={row.errors.some(e => e.includes('date')) ? 'error' : ''}
                        readOnly={isViewMode}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        value={row.narration}
                        onChange={(e) => handleIncomeRowChange(row.tempId, 'narration', e.target.value)}
                        placeholder="Description/notes"
                        readOnly={isViewMode}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        value={row.amount || ''}
                        onChange={(e) => handleIncomeRowChange(row.tempId, 'amount', parseFloat(e.target.value) || 0)}
                        onWheel={(e) => e.currentTarget.blur()} // Prevent scroll wheel changes
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className={row.errors.some(e => e.includes('Amount')) ? 'error' : ''}
                        readOnly={isViewMode}
                      />
                    </td>
                    <td>
                      <button
                        className="btn-icon delete"
                        onClick={() => removeIncomeRow(row.tempId)}
                        disabled={incomeRows.length === 1 || isViewMode}
                        title="Remove Row"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="total-row">
                  <td colSpan={3}><strong>Entries Count:</strong></td>
                  <td><strong>{incomeRows.length} entries</strong></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
          
          {/* Show errors for each row */}
          {incomeRows.some(row => row.errors.length > 0) && (
            <div className="form-errors">
              <h4>Please fix the following errors:</h4>
              {incomeRows.map((row, index) => 
                row.errors.length > 0 && (
                  <div key={row.tempId} className="row-errors">
                    <strong>Row {index + 1}:</strong>
                    <ul>
                      {row.errors.map((error, errorIndex) => (
                        <li key={errorIndex}>{error}</li>
                      ))}
                    </ul>
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}

      {/* Summary and Actions */}
      {selectedProject && (
        <div className="form-container">
          <div className="summary-section">
            <h3>Summary</h3>
            <div className="summary-grid">
              <div className="summary-item">
                <span className="summary-label">Project:</span>
                <span className="summary-value">{selectedProject.name}</span>
              </div>
              <div className="summary-item">
                <span className="summary-label">Total Entries:</span>
                <span className="summary-value">{incomeRows.length}</span>
              </div>
              {isHiddenModeActive && (
                <div className="summary-item">
                  <span className="summary-label">Database:</span>
                  <span className="summary-value amount" style={{color: '#dc3545'}}>Receivables (Encrypted)</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="form-actions">
            <button 
              className="btn-secondary"
              onClick={() => navigate('/ledger/other')}
            >
              {isViewMode ? 'Back' : 'Cancel'}
            </button>
            {!isViewMode && (
              <button 
                className="btn-primary"
                onClick={handleSubmit}
                disabled={submitting || incomeRows.length === 0}
              >
                {submitting ? 
                  (mode === 'edit' ? 'Updating...' : 'Creating...') : 
                  (mode === 'edit' ? 'Update Other Income' : 'Create Other Income Entries')
                }
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AddOtherIncome;
